import React from 'react';
import { motion } from 'framer-motion';
import { Linkedin, Instagram, Mail, FileText, Sparkles, ArrowRight, CheckCircle, PenLine } from 'lucide-react';
import Section from './Section';

const contentBreakdown = [
  { icon: Linkedin, platform: 'LinkedIn Post', color: '#0A66C2', count: 1 },
  { icon: PenLine, platform: 'Short Posts', color: '#10B981', count: 3 },
  { icon: Instagram, platform: 'Instagram Caption', color: '#E4405F', count: 1 },
  { icon: Mail, platform: 'Newsletter', color: '#6366F1', count: 1 },
  { icon: FileText, platform: 'Blog Snippets', color: '#8B5CF6', count: 2 },
];

const CaseBlock = () => {
  return (
    <Section id="case-example" background="alt" data-testid="case-block-section">
      <div className="max-w-5xl mx-auto">
        {/* Header */}
        <div className="text-center mb-10">
          <motion.span 
            className="inline-flex items-center gap-2 px-3 py-1.5 bg-pink-50 rounded-full text-sm font-medium text-pink-600 mb-4"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <Sparkles className="w-4 h-4" />
            Real Example
          </motion.span>
          <motion.h2 
            className="text-3xl md:text-4xl font-bold text-slate-800 mb-4"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.1 }}
          >
            One Product Update → 8 Pieces of Content
          </motion.h2>
          <motion.p 
            className="text-slate-500 text-lg max-w-2xl mx-auto"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.2 }}
          >
            See how a single announcement transforms into a week of distribution.
          </motion.p>
        </div>

        {/* Visual Breakdown */}
        <motion.div 
          className="bg-white rounded-3xl border border-slate-200 shadow-xl overflow-hidden"
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ delay: 0.3 }}
        >
          {/* Source Input */}
          <div className="bg-gradient-to-r from-indigo-50 to-violet-50 p-6 border-b border-slate-200">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 bg-gradient-to-br from-indigo-600 to-violet-600 rounded-xl flex items-center justify-center shrink-0">
                <FileText className="w-6 h-6 text-white" />
              </div>
              <div>
                <p className="text-xs text-indigo-600 font-semibold uppercase tracking-wider mb-1">Input: Product Update</p>
                <h3 className="text-lg font-bold text-slate-800">New Feature Release: AI Content Scheduling</h3>
                <p className="text-sm text-slate-500 mt-1">500-word product<br className="md:hidden" />{' '}announcement draft</p>
              </div>
            </div>
          </div>

          {/* Transformation Indicator */}
          <div className="flex items-center justify-center py-4 bg-slate-50">
            <div className="flex items-center gap-2 px-4 py-2 bg-white rounded-full border border-slate-200 shadow-sm">
              <Sparkles className="w-4 h-4 text-violet-500" />
              <span className="text-sm font-medium text-slate-600">AI transforms into</span>
              <ArrowRight className="w-4 h-4 text-violet-500" />
            </div>
          </div>

          {/* Output Breakdown */}
          <div className="p-6">
            <div className="grid sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4">
              {contentBreakdown.map((item, index) => (
                <motion.div
                  key={item.platform}
                  className="bg-slate-50 rounded-xl p-4 text-center hover:bg-white hover:shadow-md transition-all duration-200 border border-transparent hover:border-slate-200"
                  initial={{ opacity: 0, scale: 0.95 }}
                  whileInView={{ opacity: 1, scale: 1 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.3, delay: 0.4 + index * 0.08 }}
                >
                  <div 
                    className="w-10 h-10 rounded-xl flex items-center justify-center mx-auto mb-3"
                    style={{ backgroundColor: `${item.color}15` }}
                  >
                    <item.icon className="w-5 h-5" style={{ color: item.color }} />
                  </div>
                  <p className="text-2xl font-bold text-slate-800 mb-1">{item.count}</p>
                  <p className="text-xs text-slate-500">{item.platform}</p>
                </motion.div>
              ))}
            </div>

            {/* Summary */}
            <div className="mt-6 pt-6 border-t border-slate-200 flex flex-wrap items-center justify-center gap-6">
              <div className="flex items-center gap-2">
                <CheckCircle className="w-5 h-5 text-emerald-500" />
                <span className="text-sm text-slate-600"><strong>8 pieces</strong> of content</span>
              </div>
              <div className="flex items-center gap-2">
                <CheckCircle className="w-5 h-5 text-emerald-500" />
                <span className="text-sm text-slate-600"><strong>5 platforms</strong> covered</span>
              </div>
              <div className="flex items-center gap-2">
                <CheckCircle className="w-5 h-5 text-emerald-500" />
                <span className="text-sm text-slate-600"><strong>30 seconds</strong> to generate</span>
              </div>
            </div>
          </div>
        </motion.div>
      </div>
    </Section>
  );
};

export default CaseBlock;
